import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, User, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ClientChat({ dossierId }) {
  const [messages, setMessages] = useState([]);
  const [advisorName, setAdvisorName] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const getToken = () => localStorage.getItem('client_token');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChat = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/dossiers/${dossierId}/chat`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessages(res.data.messages || []);
      setAdvisorName(res.data.advisor_name || 'Conseiller');
      setChatReady(true);
    } catch (err) {
      console.error('Chat fetch error:', err);
    }
  }, [dossierId]);

  const pollMessages = useCallback(async () => {
    if (!chatReady) return;
    try {
      const lastTs = messages.length > 0 ? messages[messages.length - 1].timestamp : null;
      const res = await axios.get(`${API_URL}/api/client/dossiers/${dossierId}/chat/poll`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: lastTs ? { after: lastTs } : {}
      });
      if (res.data.messages?.length > 0) {
        setMessages(prev => [...prev, ...res.data.messages]);
      }
    } catch (err) {
      // Silent poll error
    }
  }, [dossierId, chatReady, messages]);

  const sendHeartbeat = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/client/dossiers/${dossierId}/chat/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
    } catch (err) {
      // Silent
    }
  }, [dossierId]);

  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll every 8s and heartbeat every 30s
  useEffect(() => {
    if (!chatReady) return;
    const pollInterval = setInterval(pollMessages, 8000);
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);
    sendHeartbeat();
    return () => {
      clearInterval(pollInterval);
      clearInterval(heartbeatInterval);
    };
  }, [chatReady, pollMessages, sendHeartbeat]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/client/dossiers/${dossierId}/chat`, {
        text: input.trim()
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setMessages(prev => [...prev, res.data.message]);
      setInput('');
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const formatDate = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' });
    } catch { return ''; }
  };

  // Group messages by date
  let lastDate = '';

  return (
    <div className="flex flex-col h-[500px] bg-card border border-border rounded-lg overflow-hidden" data-testid="client-chat">
      {/* Chat header */}
      <div className="bg-primary px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-white text-sm font-semibold">{advisorName}</p>
          <p className="text-white/60 text-xs">Votre conseiller</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
          <span className="text-white/60 text-xs">En ligne</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-secondary/30">
        {messages.map((msg, i) => {
          const msgDate = formatDate(msg.timestamp);
          let showDate = false;
          if (msgDate !== lastDate) {
            showDate = true;
            lastDate = msgDate;
          }

          return (
            <React.Fragment key={msg.id || i}>
              {showDate && (
                <div className="text-center my-2">
                  <span className="text-xs text-muted-foreground bg-background px-3 py-1 rounded-full">{msgDate}</span>
                </div>
              )}
              
              {msg.sender === 'system' ? (
                <div className="text-center">
                  <span className="text-xs text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
                    {msg.text}
                  </span>
                </div>
              ) : msg.sender === 'advisor' ? (
                <div className="flex items-end gap-2 max-w-[85%]">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-3 h-3 text-primary" />
                  </div>
                  <div>
                    <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{msg.text}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 ml-1">{formatTime(msg.timestamp)}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2 max-w-[85%] ml-auto flex-row-reverse">
                  <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-3 h-3 text-accent" />
                  </div>
                  <div>
                    <div className="bg-accent text-white rounded-2xl rounded-br-sm px-3.5 py-2.5">
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 mr-1 text-right">{formatTime(msg.timestamp)}</p>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 bg-white border-t border-border flex items-center gap-2 flex-shrink-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Votre message..."
          className="flex-1 px-3 py-2 border border-border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          disabled={sending}
          data-testid="chat-input"
        />
        <button
          onClick={handleSend}
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/90 disabled:opacity-50 flex-shrink-0"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default ClientChat;
